import { useState } from 'react';
import { Plan } from '../services/companyService';
import { upgradePlan } from '../services/planUpgradeService';
import { useAuth } from '../contexts/AuthContext';
import { getLoggedInUserData } from '../helpers/storageUtils';

interface UsePlanUpgradeOptions {
  currentPlanCode?: string;
  currentPlanName?: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const usePlanUpgrade = (options: UsePlanUpgradeOptions = {}) => {
  const { user } = useAuth();
  const [isUpgrading, setIsUpgrading] = useState(false);

  const handlePlanSelect = async (
    planId: string,
    availablePlans: Plan[],
    onModalClose?: () => void
  ) => {
    const selectedPlan = availablePlans.find((plan) => plan.id === planId);
    if (!selectedPlan) {
      const errorMsg = 'Selected plan not found';
      console.error(errorMsg);
      options.onError?.(errorMsg);
      return;
    }

    // Check if it's the current plan
    const isCurrentPlan =
      (options.currentPlanCode && options.currentPlanCode === selectedPlan.planCode) ||
      (options.currentPlanName && options.currentPlanName === selectedPlan.name);

    if (isCurrentPlan) {
      onModalClose?.();
      return;
    }

    try {
      setIsUpgrading(true);
      onModalClose?.();

      // Get customer ID and company name from logged-in user data
      const loggedInUserData = getLoggedInUserData();
      const customerId = loggedInUserData?.company_id || '';
      const customerCompanyName = loggedInUserData?.company_name || loggedInUserData?.companyName || '';

      // Use the common plan upgrade service
      const upgradeRequest = {
        planId: selectedPlan.id,
        planName: selectedPlan.name,
        planCode: selectedPlan.planCode,
        customerId: customerId, // Include customer ID (company_id) from logged-in user data
        customerEmail: user?.email || '',
        customerCompanyName: customerCompanyName, // Include company name from logged-in user data
        isPlanChange: true,
      };

      const response = await upgradePlan(upgradeRequest);

      if (response.success && response.hostedPageUrl) {
        options.onSuccess?.();
        window.location.href = response.hostedPageUrl;
      } else {
        throw new Error(response.error || 'Failed to create payment page');
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to create payment page';
      console.error('Payment page creation error:', error);
      options.onError?.(errorMsg);
      // Re-throw so caller can handle reopening modal if needed
      throw error;
    } finally {
      setIsUpgrading(false);
    }
  };

  return {
    handlePlanSelect,
    isUpgrading,
  };
};

